/**
 * Native app download targets surfaced on the `/app` entry route.
 *
 * The Android APK downloads from our own domain via the server route
 * `/download/android` (server/index.js), which serves a bundled APK or streams
 * the latest release built by the `Build Android APK` GitHub Actions workflow
 * (.github/workflows/android-build.yml). Override any value at build time:
 *
 *   REACT_APP_ANDROID_APK_URL=https://.../app.apk
 *   REACT_APP_PLAY_STORE_URL=https://play.google.com/store/apps/details?id=...
 *   REACT_APP_IOS_APP_STORE_URL=https://apps.apple.com/app/id...
 */
export interface AppDownloads {
  /** Direct .apk download (sideload). Empty string hides the button. */
  androidApkUrl: string;
  /** Google Play listing. Empty string hides the button. */
  playStoreUrl: string;
  /** Apple App Store listing. Empty string hides the button. */
  iosAppStoreUrl: string;
}

export const appDownloads: AppDownloads = {
  androidApkUrl:
    process.env.REACT_APP_ANDROID_APK_URL || '/download/android',
  playStoreUrl: process.env.REACT_APP_PLAY_STORE_URL || '',
  iosAppStoreUrl: process.env.REACT_APP_IOS_APP_STORE_URL || '',
};

export type DevicePlatform = 'android' | 'ios' | 'desktop';

/** Best-effort device platform from the user agent (browser only). */
export function detectPlatform(): DevicePlatform {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua) || isIPadOS()) return 'ios';
  return 'desktop';
}

/** iPadOS 13+ reports as desktop Safari; detect via touch + Mac platform. */
function isIPadOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    navigator.platform === 'MacIntel' &&
    (navigator as any).maxTouchPoints > 1
  );
}

/** True when running inside the Capacitor native shell. */
export function isNativeApp(): boolean {
  return Boolean((window as any)?.Capacitor?.isNativePlatform?.());
}
